import { el, icon } from './dom.js';

/**
 * Three, two, one, go (PLAYTEST_LOG 2026-10-30): a player's friend found the
 * clock starting the moment the doors open too fast. The shift is in sight and
 * still, while a lift's floor indicator counts down in the middle of it, so
 * there is a moment to see how many floors there are and who is waiting.
 *
 * Presentation only: the sim is not stepped while it counts, so the shift and
 * its replay are exactly what they were. It counts in the game's frames, so a
 * pause stops it too. Taps go through it: a first stop can be chosen early.
 */

/** Each of 3, 2, 1, then how long "go" stays. */
export const BEAT_MS = 650;
export const GO_MS = 450;
export const COUNTDOWN_MS = BEAT_MS * 3 + GO_MS;

/** What the window shows with `left` ms to go: 3, 2, 1, then 0 for go. */
export function beatAt(left: number): number {
  if (left <= GO_MS) return 0;
  return Math.min(3, Math.ceil((left - GO_MS) / BEAT_MS));
}

export class Countdown {
  readonly root: HTMLElement;
  private readonly face: HTMLElement;
  private left = 0;
  private shown = -1;

  /** `beat` sounds each step: 3, 2, 1, then 0 for go. */
  constructor(private readonly beat: (step: number) => void) {
    this.face = el('span', { class: 'countdown-face' });
    this.root = el('div', { class: 'countdown', 'aria-live': 'assertive', hidden: true }, [
      el('div', { class: 'countdown-window' }, [this.face]),
    ]);
  }

  get counting(): boolean {
    return this.left > 0;
  }

  start(): void {
    this.left = COUNTDOWN_MS;
    this.shown = -1;
    this.root.hidden = false;
    this.show();
  }

  /** Gone at once: the shift left, or begun again. */
  stop(): void {
    this.left = 0;
    this.shown = -1;
    this.root.hidden = true;
  }

  /** Moves the count on; false once it is over and the clock may start. */
  advance(deltaMs: number): boolean {
    if (this.left <= 0) return false;
    this.left -= deltaMs;
    if (this.left <= 0) {
      this.stop();
      return false;
    }
    this.show();
    return true;
  }

  private show(): void {
    const step = beatAt(this.left);
    if (step === this.shown) return;
    this.shown = step;
    this.root.dataset.step = String(step);
    // A new element each step, so its pop plays again.
    this.face.replaceChildren(
      step === 0
        ? el('span', { class: 'countdown-go' }, [icon('up'), 'Go'])
        : el('span', { class: 'countdown-digit', text: String(step) }),
    );
    this.beat(step);
  }
}
